"use client";

import React, { useState, useCallback } from 'react';
import SolicitudManagement from './SolicitudManagement';
import { Solicitud } from '@/types/solicitud';
import { Button } from '@/components/ui/Button';
import { solicitudService } from '@/lib/api/solicitudService';
import { emailConfigService } from '@/lib/api/emailConfigService';
import { EmailConfigModal } from './EmailConfigModal';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';

const hasValue = (value?: string | null) => Boolean(value && String(value).trim() !== '');

const SolicitarVoBosManagement: React.FC = () => {
  const { user, checkPermission } = useAuth();
  const [isSysAdmin, setIsSysAdmin] = useState(false);

  // Evaluar permisos client-side para evitar mismatch SSR/hidratación en producción
  React.useEffect(() => {
    setIsSysAdmin(checkPermission('sysAdmin'));
  }, [checkPermission]);
  const [vistaActual, setVistaActual] = useState<'listado' | 'control' | 'envio'>('listado');
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  // Cargar solicitudes con endpoint filtrado por estatus server-side
  const loadSolicitudes = async () => {
    if (!user?.sub) return;
    setIsLoading(true);
    setError(null);
    const res = await solicitudService.getEntregadosARiesgos(user.sub);
    if (res.data) setSolicitudes(res.data);
    else setError(res.error || 'Error al cargar solicitudes');
    setIsLoading(false);
  };

  // customFetch para SolicitudManagement - usa endpoint filtrado server-side
  const customFetch = useCallback(async (setSols: (s: Solicitud[]) => void) => {
    if (!user?.sub) return;
    const res = await solicitudService.getEntregadosARiesgos(user.sub);
    if (res.data) setSols(res.data);
  }, [user?.sub]);

  // Carga inicial solo si entramos a control o envío, 
  // pero para simplificar cargamos siempre que se necesite
  React.useEffect(() => {
    if (vistaActual === 'control' || vistaActual === 'envio') {
      loadSolicitudes();
    }
  }, [vistaActual]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>, list: Solicitud[]) => {
    if (e.target.checked) {
      setSelectedIds(new Set(list.map(s => s.id!)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newCtx = new Set(selectedIds);
    if (newCtx.has(id)) newCtx.delete(id);
    else newCtx.add(id);
    setSelectedIds(newCtx);
  };

  const handleEnviarVoBos = async () => {
    if (selectedIds.size === 0) return;
    setIsLoading(true);
    try {
      const fechaHoy = new Date().toISOString();
      // Enviar una por una (o endpoint masivo si existiera)
      // La lógica legacy determina cual VoBo toca (1, 2 o 3) basado en cuales ya están llenos.
      // Aquí, como es frontend, lo ideal es que el backend decida, O enviamos a un endpoint especifico.
      // Simularemos la lógica "inteligente" iterando.

      const promises = Array.from(selectedIds).map(async (id) => {
        const s = solicitudes.find(sol => sol.id === id);
        if (!s) return;

        let campoAActualizar = 'fechaPrimerSolicitudVoBo';
        if (s.fechaPrimerSolicitudVoBo) campoAActualizar = 'fechaSegundaSolicitudVoBo';
        if (s.fechaSegundaSolicitudVoBo) campoAActualizar = 'fechaTercerSolicitudVoBo';

        // Si ya tiene las 3, no hacemos nada (o mostramos error)
        if (s.fechaTercerSolicitudVoBo) return;

        return solicitudService.updateField(id, campoAActualizar, fechaHoy, 'Solicitar VoBos');
      });

      await Promise.all(promises);

      // Enviar correos si hay configuración activa
      setEmailStatus(null);
      const ids = Array.from(selectedIds);
      const emailRes = await emailConfigService.sendVoBo(ids);
      if (emailRes.data) {
        if (emailRes.data.ok) {
          setEmailStatus(`Correos enviados: ${emailRes.data.sent}`);
        } else if (emailRes.data.sent > 0) {
          setEmailStatus(`Correos enviados: ${emailRes.data.sent}. Errores: ${emailRes.data.errors.join('; ')}`);
        } else {
          // No falla si no hay config activa — solo omite el envío silenciosamente
          if (emailRes.data.errors.length > 0) {
            setEmailStatus(`Sin correos: ${emailRes.data.errors[0]}`);
          }
        }
      }

      // Recargar y limpiar selección
      await loadSolicitudes();
      setSelectedIds(new Set());
      setVistaActual('control'); // Ir a control para ver el resultado
    } catch (err) {
      setError('Error al enviar VoBos');
    } finally {
      setIsLoading(false);
    }
  };

  const extraFilter = (s: Solicitud) => {
    // Legacy: ENTREGA A RIESGOS + análisis concluido + VoBo aún no registrado
    return hasValue(s.fechaAnalisisConluido) && !hasValue(s.fechaVoBo);
  };

  // Obtener último envío de VoBo
  const getUltimoEnvio = (s: Solicitud): string => {
    if (hasValue(s.fechaTercerSolicitudVoBo)) return s.fechaTercerSolicitudVoBo!;
    if (hasValue(s.fechaSegundaSolicitudVoBo)) return s.fechaSegundaSolicitudVoBo!;
    if (hasValue(s.fechaPrimerSolicitudVoBo)) return s.fechaPrimerSolicitudVoBo!;
    return '';
  };

  // Vista de Control de Envíos
  const ControlDeEnvios = () => {
    const envios = solicitudes.filter(s =>
      hasValue(s.fechaPrimerSolicitudVoBo) ||
      hasValue(s.fechaSegundaSolicitudVoBo) ||
      hasValue(s.fechaTercerSolicitudVoBo) ||
      hasValue(s.fechaVoBo)
    );

    const formatCurrency = (v?: number | null) =>
      new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Control de Envíos de VoBos</h2>
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Fecha de Solicitud</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Entregado a Riesgos</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Monto Autorizado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Plazo</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase">1ro</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase">2do</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase">3ro</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Último Envío</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {envios.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">{s.fechaContacto || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.fechaEntregaARiesgos || '-'}</td>
                    <td className="px-4 py-3 text-gray-900 font-medium">{s.clienteEmpresa || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{`${s.clienteNombre || ''} ${s.clienteApellidoPaterno || ''} ${s.clienteApellidoMaterno || ''}`.trim() || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(s.montoAutorizado)}</td>
                    <td className="px-4 py-3 text-gray-700">{s.plazoAutorizado || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={hasValue(s.fechaPrimerSolicitudVoBo) ? 'text-green-600 font-bold' : 'text-gray-400'}>
                        {hasValue(s.fechaPrimerSolicitudVoBo) ? 'Si' : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={hasValue(s.fechaSegundaSolicitudVoBo) ? 'text-green-600 font-bold' : 'text-gray-400'}>
                        {hasValue(s.fechaSegundaSolicitudVoBo) ? 'Si' : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={hasValue(s.fechaTercerSolicitudVoBo) ? 'text-green-600 font-bold' : 'text-gray-400'}>
                        {hasValue(s.fechaTercerSolicitudVoBo) ? 'Si' : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{getUltimoEnvio(s) || '-'}</td>
                  </tr>
                ))}
                {envios.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                      No hay solicitudes con envío de VoBo registrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Vista de Listado de Envío
  const ListadoDeEnvio = () => {
    // Filtramos: extraFilter + excluir las que ya tienen 3er VoBo (ya no se pueden enviar más)
    const listado = solicitudes.filter(s => extraFilter(s) && !hasValue(s.fechaTercerSolicitudVoBo));

    // Nivel de VoBo con color diferenciado
    const getVoBoStatus = (s: Solicitud): { label: string; cls: string } => {
      if (hasValue(s.fechaSegundaSolicitudVoBo)) return { label: 'Pendiente VoBo 3', cls: 'bg-red-100 text-red-700' };
      if (hasValue(s.fechaPrimerSolicitudVoBo)) return { label: 'Pendiente VoBo 2', cls: 'bg-orange-100 text-orange-700' };
      return { label: 'Pendiente VoBo 1', cls: 'bg-yellow-100 text-yellow-800' };
    };

    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Listado de Envío de VoBos</h2>
        {error && <ErrorMessage message={error} />}

        <div className="space-y-4">
          <p className="text-gray-600">
            Seleccione las solicitudes para enviar a autorización (VoBo).
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      onChange={(e) => handleSelectAll(e, listado)}
                      checked={listado.length > 0 && selectedIds.size === listado.length}
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Monto Autorizado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Plazo</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Jefe Inmediato</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Correo Jefe</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase">Nivel VoBo</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {listado.map((s) => {
                  const vobo = getVoBoStatus(s);
                  return (
                    <tr key={s.id} className={`${selectedIds.has(s.id!) ? 'bg-blue-50' : ''} hover:bg-gray-50 transition-colors`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={selectedIds.has(s.id!)}
                          onChange={() => handleSelectOne(s.id!)}
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{s.clienteEmpresa || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{`${s.clienteNombre || ''} ${s.clienteApellidoPaterno || ''} ${s.clienteApellidoMaterno || ''}`.trim() || '-'}</td>
                      <td className="px-4 py-3 font-medium">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(s.montoAutorizado || 0)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{s.plazoAutorizado || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{s.clienteNombreJefe || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{s.clienteCorreoJefe || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${vobo.cls}`}>
                          {vobo.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {listado.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      No hay solicitudes pendientes de envío de VoBo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <div className="flex-1 flex items-center text-sm text-gray-500">
              {selectedIds.size} seleccionados
            </div>
            <Button variant="secondary" onClick={() => setVistaActual('listado')}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleEnviarVoBos}
              disabled={isLoading || selectedIds.size === 0}
            >
              {isLoading ? 'Enviando...' : 'Enviar VoBos Seleccionados'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <EmailConfigModal isOpen={showEmailConfig} onClose={() => setShowEmailConfig(false)} />

      {/* Botones de navegación entre vistas (como en legacy) */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-wrap gap-3 justify-between items-center">
        <div className="flex gap-3 flex-wrap">
          <Button
            variant={vistaActual === 'listado' ? 'primary' : 'secondary'}
            onClick={() => setVistaActual('listado')}
          >
            Listado de Solicitudes
          </Button>
          <Button
            variant={vistaActual === 'control' ? 'primary' : 'secondary'}
            onClick={() => setVistaActual('control')}
          >
            Control de Envíos
          </Button>
          <Button
            variant={vistaActual === 'envio' ? 'primary' : 'secondary'}
            onClick={() => setVistaActual('envio')}
          >
            Listado de Envío
          </Button>
        </div>
        {isSysAdmin && (
          <Button variant="secondary" onClick={() => setShowEmailConfig(true)}>
            ✉ Configurar Correo
          </Button>
        )}
      </div>

      {emailStatus && (
        <div className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm">
          {emailStatus}
        </div>
      )}

      {/* Contenido según la vista seleccionada */}
      {vistaActual === 'listado' && (
        <SolicitudManagement
          title="Solicitar VoBos"
          fixedStatus="ENTREGA A RIESGOS"
          extraFilter={extraFilter}
          customFetch={customFetch}
          modulo="vobo"
          showNewButton={false}
          montoColumn="autorizado"
        />
      )}

      {vistaActual === 'control' && <ControlDeEnvios />}

      {vistaActual === 'envio' && <ListadoDeEnvio />}
    </div>
  );
};

export default SolicitarVoBosManagement;
